import { ReactElement, useEffect } from 'react';
import { useUIDSeed } from 'react-uid';
import { HeapCustomEvent } from 'config';
import HeapAnalytics from 'services/HeapAnalytics';

import useFavorites from 'hooks/useFavorites';
import useCurrentUser from 'hooks/useCurrentUser';
import useAsyncEffect from 'hooks/useAsyncEffect';

import RecipeSkeleton from 'components/shared/skeletons/RecipeSkeleton';
import SkuItem from 'components/shared/SkuGallery/SkuItem';
import FetchState from 'models/FetchState';
import MenuItem from 'models/MenuItem';
import ISku from 'models/ISku';
import EmptyFavorites from './EmptyFavorites';

function Favorites(): ReactElement {
  const seed = useUIDSeed();

  const {
    currentUser,
    fetchState: fetchUserDataState,
    reload: reloadUser,
  } = useCurrentUser();

  const { id: userId, favorite_count: favoriteCount } = currentUser;
  const { favorites, fetchState: fetchFavoriteDataState } = useFavorites();

  const handleClick = (item: MenuItem<ISku>) => {
    const { sku } = item;
    HeapAnalytics.track(
      HeapCustomEvent.FAVORITE_RECIPE_PAGE_VISITED,
      {
        user_id: userId,
        recipe_id: sku.product_id,
        recipe_url: sku.link_to_product,
        product_line: sku.product_line_name,
      },
    );
  };

  useAsyncEffect(async (isMounted) => {
    if (isMounted.current) reloadUser();
  }, []);

  useEffect(() => {
    if (userId) {
      HeapAnalytics.track(
        HeapCustomEvent.FAVORITE_PAGE_VISITED,
        { user_id: userId },
      );
    }
  }, [userId]);

  return (
    <div className="col-md-10 col-lg-10">
      <div className="c-myfavorites">
        <div className="c-myfavorites__header">
          <h1 className="c-heading c-heading--center c-heading--brand-4">
            My Favorites
          </h1>
          <p className="u-text--center">
            Save your favorite recipes for easy
            access and come back to them anytime.
          </p>
        </div>

        {fetchUserDataState === FetchState.SUCCESS && (
          <>
            {favoriteCount ? (
              <>
                {fetchFavoriteDataState === FetchState.IN_PROGRESS && (
                  <div className="c-recipe-gallery">
                    <div className="row">
                      {[...Array(favoriteCount)].map((el, i) => (
                        <div
                          key={seed(i)}
                          className="col-12 col-sm-6 col-lg-4"
                        >
                          <RecipeSkeleton />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {fetchFavoriteDataState === FetchState.SUCCESS && (
                  <div className="c-recipe-gallery">
                    <div className="row">
                      {favorites.map((item, index) => (
                        <SkuItem
                          key={seed(index)}
                          item={item}
                          isFavoritesGallery
                          hasFavoritesEnabled
                          onClick={() => handleClick(item)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <EmptyFavorites />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Favorites;
